import { MouseEvent, type ReactElement, useRef } from 'react';
import type { Row } from '@tanstack/react-table';

import { Tooltip, TooltipContent, TooltipTrigger } from '@crossnokaye/ux-common';

import { Checkbox } from '@/ux-common/components/checkbox/checkbox';

interface SelectCellProps<TData> {
  row: Row<TData>;
  /** Tooltip shown when the checkbox is disabled. Can be a static string or a function. */
  disabledTooltip?: string | ((row: TData) => string | undefined);
}

/**
 * Row checkbox for individual row selection.
 *
 * Stops click propagation to prevent triggering row click handlers
 * when clicking the checkbox. Shows a tooltip when the row cannot be selected.
 */
export const SelectCell = <TData,>({
  row,
  disabledTooltip,
}: SelectCellProps<TData>): ReactElement => {
  const isSelected = row.getIsSelected();
  const canSelect = row.getCanSelect();
  const ref = useRef<HTMLButtonElement>(null);

  // Resolve tooltip text for disabled rows
  const tooltipText =
    !canSelect && disabledTooltip
      ? typeof disabledTooltip === 'function'
        ? disabledTooltip(row.original)
        : disabledTooltip
      : undefined;

  const handleCellClick = (e: MouseEvent<HTMLDivElement>) => {
    if (canSelect) {
      ref.current?.click();
    }
    e.stopPropagation();
  };

  const checkbox = (
    <div
      className="flex items-center justify-center w-full h-full"
      onMouseDown={(e) => {
        // Prevent table drag-scroll from treating checkbox clicks as drag starts.
        e.stopPropagation();
      }}
      onPointerDown={(e) => {
        // Keep behavior consistent for pointer events (touch/pen/mouse).
        e.stopPropagation();
      }}
      onClick={handleCellClick}
      role="button"
    >
      <Checkbox
        ref={ref}
        checked={isSelected}
        disabled={!canSelect}
        onCheckedChange={(checked) => {
          row.toggleSelected(checked === true);
        }}
        aria-label={`Select row ${row.index + 1}`}
      />
    </div>
  );

  if (tooltipText) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{checkbox}</TooltipTrigger>
        <TooltipContent side="right">{tooltipText}</TooltipContent>
      </Tooltip>
    );
  }

  return checkbox;
};

SelectCell.displayName = 'SelectCell';
